// The audio socket, /v1/session/audio (D-35; ADR-026). The client streams 16 kHz mono 16-bit PCM in
// 20 ms binary frames, exactly AUDIO_FRAME_BYTES each, which carry no schema; its text frames are the
// three below. The server sends nothing on this socket but WebSocket close codes: the transcript
// travels on the main socket as `transcript` events, and a final transcript is the utterance.
import { z } from "zod";
import { NonEmpty, strictObject } from "../primitives.js";
import { CLIENT_EVENT_MAX_BYTES, summariseIssues, utf8Length } from "./codec.js";
import { ERROR_CODES } from "./errors.js";
export const AUDIO_SAMPLE_RATE_HZ = 16000;
export const AUDIO_CHANNELS = 1;
export const AUDIO_SAMPLE_BYTES = 2;
export const AUDIO_FRAME_MS = 20;
/** One binary frame: 16000 samples/s × 1 channel × 2 bytes × 20 ms = 640 bytes. */
export const AUDIO_FRAME_BYTES = (AUDIO_SAMPLE_RATE_HZ * AUDIO_CHANNELS * AUDIO_SAMPLE_BYTES * AUDIO_FRAME_MS) / 1000;
export const AudioClientFrame = z.discriminatedUnion("t", [
    strictObject({ t: z.literal("start"), utteranceId: NonEmpty, viewId: NonEmpty }),
    strictObject({ t: z.literal("end") }),
    strictObject({ t: z.literal("abort") }),
]);
const invalid = (message) => ({
    ok: false,
    error: {
        code: "audio.invalid-frame",
        retryable: ERROR_CODES["audio.invalid-frame"].retryable,
        message,
    },
});
/** A text frame for the audio socket. Every frame is a few bytes, far under the client limit. */
export const encodeAudioFrame = (frame) => JSON.stringify(frame);
/**
 * A binary frame from the audio socket: exactly AUDIO_FRAME_BYTES of PCM, or audio.invalid-frame.
 * The bytes carry no schema; only the length is checked. Never throws.
 */
export function checkAudioPcmFrame(data) {
    try {
        const pcm = data instanceof ArrayBuffer
            ? new Uint8Array(data)
            : ArrayBuffer.isView(data)
                ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
                : undefined;
        if (pcm === undefined)
            return invalid("audio binary frame is not bytes");
        if (pcm.byteLength !== AUDIO_FRAME_BYTES)
            return invalid(`audio binary frame is ${String(pcm.byteLength)} bytes, not ${String(AUDIO_FRAME_BYTES)}`);
        return { ok: true, pcm };
    }
    catch {
        return invalid("audio binary frame could not be checked");
    }
}
/**
 * A text frame from the audio socket: the frame, or audio.invalid-frame. Never throws. The byte
 * length is checked before JSON.parse, at the main socket's client limit (D-49).
 */
export function decodeAudioFrame(text) {
    try {
        if (typeof text !== "string")
            return invalid("audio text frame is not text");
        if (text.length > CLIENT_EVENT_MAX_BYTES ||
            utf8Length(text, CLIENT_EVENT_MAX_BYTES) > CLIENT_EVENT_MAX_BYTES)
            return invalid(`audio text frame is over ${String(CLIENT_EVENT_MAX_BYTES)} bytes`);
        let value;
        try {
            value = JSON.parse(text);
        }
        catch {
            return invalid("audio text frame is not JSON");
        }
        const r = AudioClientFrame.safeParse(value);
        if (!r.success)
            return invalid(summariseIssues(r.error.issues));
        return { ok: true, frame: r.data };
    }
    catch {
        return invalid("audio text frame could not be validated");
    }
}
//# sourceMappingURL=audio.js.map